import { Link, useParams } from "react-router-dom";
import { useSite } from "../site/SiteContext";
import { ArrowLeft } from "lucide-react";
import data from "../content/case-studies.json";
import { isPlaceholder } from "../content/services";
import { StatBlock } from "../components/StatBlock";
import { SnapshotTable } from "../components/SnapshotTable";
import { CaseChart } from "../components/CaseChart";
import { Gallery } from "../components/Gallery";
import { CaseCard, LogoTile } from "../components/CaseCard";
import { CTABand } from "../components/Buttons";
import { WistiaEmbed } from "../components/WistiaEmbed";
import { EmailShowcase } from "../components/EmailShowcase";
import { useMeta } from "../hooks/useMeta";

function Section({ title, bold, after, children, testId, light = false }) {
  return (
    <section data-testid={testId} className={light ? "bg-fog" : "bg-white"}>
      <div className="mx-auto max-w-4xl px-5 py-12 md:py-16">
        {title && (
          <h2 className="text-[26px] font-normal leading-[1.15] text-navy md:text-[32px]">
            {title}
            {bold && <strong className="font-bold">{bold}</strong>}
            {after}
          </h2>
        )}
        <div className={title ? "mt-6" : ""}>{children}</div>
      </div>
    </section>
  );
}

function NotFound() {
  const { casesBase, homePath } = useSite();
  return (
    <main data-testid="case-not-found" className="bg-white">
      <div className="mx-auto max-w-4xl px-5 py-24 text-center">
        <h1 className="text-3xl font-bold text-navy">This case study is not published yet.</h1>
        <Link to={casesBase || homePath} data-testid="not-found-home-link" className="mt-6 inline-block font-bold text-link hover:underline">
          Back to all results
        </Link>
      </div>
    </main>
  );
}

export default function CaseStudy() {
  const { casesBase, homePath } = useSite();
  const { slug } = useParams();
  const cs = data.cases.find((c) => c.slug === slug);
  const published = cs && cs.status === "published";

  useMeta({
    title: published
      ? `${cs.client}: ${cs.headline} | Smart Marketer Agency results`
      : "Case study | Smart Marketer Agency results",
    description: published ? cs.sub : "",
    image: published && cs.heroImage ? cs.heroImage : undefined,
  });

  if (!published) return <NotFound />;

  const stats = (cs.heroStats || []).filter((s) => !isPlaceholder(s.value));
  const showStats = stats.length > 0 && cs.heroVariant !== "testimonial";
  const testimonial = cs.testimonial && !isPlaceholder(cs.testimonial.quote) ? cs.testimonial : null;
  const moves = (cs.moves || []).filter((m) => !isPlaceholder(m.title));
  const galleryTop = cs.galleryPosition === "top" && cs.gallery && cs.gallery.length > 0;
  const related = data.cases
    .filter((c) => c.slug !== cs.slug && c.status === "published")
    .sort((a, b) => (b.services.some((s) => cs.services.includes(s)) ? 1 : 0) - (a.services.some((s) => cs.services.includes(s)) ? 1 : 0))
    .slice(0, 3);

  return (
    <main data-testid={`case-page-${cs.slug}`}>
      <section className="bg-navy">
        <div className="mx-auto max-w-4xl px-5 py-14 md:py-20">
          <Link to={casesBase || homePath} data-testid="case-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-white/60 hover:text-white">
            <ArrowLeft size={16} /> All results
          </Link>
          <div className="mt-8"><LogoTile logo={cs.logo} logoLight={cs.logoLight} name={cs.client} light className="h-12 max-w-[220px]" /></div>
          <div className="mt-4 flex flex-wrap gap-2">
            {cs.services.filter((s) => !isPlaceholder(s)).map((s) => (
              <span key={s} className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white">{s}</span>
            ))}
            {(cs.platforms || []).map((p) => (
              <span key={p} className="rounded-full border border-white/20 px-3 py-1 text-xs font-medium text-white/60">{p}</span>
            ))}
          </div>
          <h1 className="mt-6 text-[34px] font-bold leading-[1.15] text-white md:text-[51px]">{cs.headline}</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">{cs.sub}</p>

          {cs.heroVariant === "testimonial" && testimonial ? (
            <figure data-testid="case-hero-testimonial" className="mt-10 rounded-xl bg-white/5 p-8">
              {testimonial.wistiaId && (
                <div className="mb-8"><WistiaEmbed id={testimonial.wistiaId} title={`${testimonial.name} testimonial`} testId="case-hero-testimonial-video" /></div>
              )}
              <blockquote className="text-xl leading-relaxed text-white md:text-2xl">"{testimonial.quote}"</blockquote>
              <figcaption className="mt-6">
                <div className="text-sm font-bold text-white">{testimonial.name}</div>
                <div className="text-xs uppercase tracking-widest text-white/50">{testimonial.title}</div>
              </figcaption>
            </figure>
          ) : showStats ? (
            <div data-testid="case-hero-stats" className="mt-10 grid grid-cols-2 gap-8 border-t border-white/10 pt-8 md:grid-cols-4">
              {stats.map((s, i) => (
                <StatBlock key={i} dark testId={`case-stat-${i}`} value={s.value} label={s.label} delta={s.delta} />
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {galleryTop && (
        <Section testId="gallery-section">
          <Gallery items={cs.gallery} />
        </Section>
      )}

      {(cs.snapshots || []).filter((s) => !isPlaceholder(s.caption)).map((snap, i) => (
        <Section key={i} testId={`snapshot-section-${i}`} light={i % 2 === 0}>
          <SnapshotTable snapshot={snap} testId={`snapshot-table-${i}`} />
        </Section>
      ))}

      {cs.designs && cs.designs.length > 0 && <EmailShowcase items={cs.designs} />}

      {cs.brand && !isPlaceholder(cs.brand) && (
        <Section title="The " bold="brand" testId="brand-section">
          <p className="text-base leading-relaxed text-navy/80 md:text-lg">{cs.brand}</p>
        </Section>
      )}

      {cs.problem && !isPlaceholder(cs.problem) && (
        <Section title="The " bold="problem" testId="problem-section" light>
          <p className="text-base leading-relaxed text-navy/80 md:text-lg">{cs.problem}</p>
        </Section>
      )}

      {moves.length > 0 && (
        <Section title="What " bold="we did" testId="moves-section">
          <ol className="space-y-6">
            {moves.map((m, i) => (
              <li key={i} className="flex gap-4" data-testid={`move-${i}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-pink text-sm font-bold text-white">
                  {i + 1}
                </span>
                <div>
                  <strong className="font-bold text-navy">{m.title}</strong>{" "}
                  <span className="text-navy/80">{m.body}</span>
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {(cs.charts || []).map((chart, i) => (
        <Section key={i} testId={`chart-section-${i}`} light={moves.length > 0}>
          <CaseChart chart={chart} />
        </Section>
      ))}

      {cs.callout && (
        <Section testId="callout-section">
          <div className="rounded-xl border-l-4 border-pink bg-fog p-7">
            <h3 className="text-lg font-bold text-navy">{cs.callout.title}</h3>
            <p className="mt-3 text-base leading-relaxed text-navy/80">{cs.callout.body}</p>
            <a href={cs.callout.linkUrl} data-testid="callout-link" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block font-bold text-link hover:underline">
              {cs.callout.linkLabel}
            </a>
          </div>
        </Section>
      )}

      {!galleryTop && cs.gallery && cs.gallery.length > 0 && (
        <Section testId="gallery-section" light>
          <Gallery items={cs.gallery} />
        </Section>
      )}

      {cs.result && !isPlaceholder(cs.result) && (
        <Section title="The " bold="result" testId="result-section">
          <p className="text-lg leading-relaxed text-navy md:text-xl">{cs.result}</p>
        </Section>
      )}

      {testimonial && cs.heroVariant !== "testimonial" && (
        <Section testId="testimonial-section" light>
          <figure className="rounded-xl border border-mist bg-white p-8">
            {testimonial.wistiaId ? (
              <div className="mb-8"><WistiaEmbed id={testimonial.wistiaId} title={`${testimonial.name} testimonial`} testId="case-testimonial-video" /></div>
            ) : testimonial.poster && (
              <img src={testimonial.poster} alt={`${testimonial.name} video testimonial`} loading="lazy" className="mb-6 w-full max-w-md rounded-xl" />
            )}
            <blockquote className="text-xl leading-relaxed text-navy md:text-2xl">"{testimonial.quote}"</blockquote>
            <figcaption className="mt-6">
              <div className="text-sm font-bold text-navy">{testimonial.name}</div>
              <div className="text-xs uppercase tracking-widest text-navy/50">{testimonial.title}</div>
            </figcaption>
          </figure>
        </Section>
      )}

      <CTABand />

      <section data-testid="more-results" className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <h2 className="text-[26px] font-normal leading-[1.15] text-navy md:text-[32px]">
            More <strong className="font-bold">results</strong>
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {related.map((r) => (
              <CaseCard key={r.slug} cs={r} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
